/**
 * Global cart rules (DK, every storefront page): "Gratis Rekvisitter" (settings.product__free_props) has quantity =
 * the number of Myselfie booths after ANY cart change, by any path, and leaves the cart with the last booth
 * (owner 2026-10-05). Loaded from layout/theme.liquid via snippets/myselfie-cart-rules.liquid, after
 * assets/myselfie-checkout.js: the reconciliation itself is KIT.syncFreeProps; this file only decides when it runs and
 * who runs it. Vanilla ES5.
 *
 * When: the server-rendered cart on load (window.myselfie.cartRules: no request when it is right); every successful
 * fetch / XHR write to /cart/add|change|update|clear (the answer is checked first - a full cart that is right, or an
 * add without a booth, costs nothing); document "myselfie:cart" with a cart; tab focus / bfcache restore.
 * Who writes (never two writers at once):
 *   - the DK cart page (assets/mys-cart.js) syncs in its own rules queue: it is only asked to re-read the cart
 *     (CartPage.reload -> runRules) when a write left the cart wrong and it is idle;
 *   - a booking module in live-cart mode (myselfie-product-selector): the sync is a job in its cart queue (serialised
 *     with its live passes and re-syncs), and the module adopts and announces the cart;
 *   - elsewhere: KIT.serial (shared with the season guard in assets/myselfie-seasons.js).
 * Never while the module or the cart page checks out (they add the free props themselves), never while the page is
 * being left, never on checkout / thank-you pages. The sync's own requests carry KIT.FREE_PROPS_TAG and are ignored
 * (no loops); safety valve: at most 4 syncs per 30 s. After a change: document "myselfie:cart" (KIT.cartDetail).
 */
(function () {
  'use strict';

  var M = (window.myselfie = window.myselfie || {});
  var KIT = M.checkoutKit;
  var CFG = M.cartRules;
  if (M.cartRulesActive || !KIT || !KIT.syncFreeProps || !KIT.freePropsOk || !CFG || !CFG.freeProps) return;
  if (/\/(checkouts?|thank_you|orders)(\/|$)/.test(location.pathname)) return;
  M.cartRulesActive = true;

  var C = { cameraType: CFG.cameraType || '', freeProps: CFG.freeProps };
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js|\.json)?\/?$/;
  var DEBOUNCE = 400;
  var WAIT = 600; // an owner (module / cart page) is still writing: look again
  var MAX_WAITS = 50; // ~30 s
  var known = null; // the latest full cart seen (write answer, "myselfie:cart"); null = unknown
  var seq = 0; // relevant foreign writes seen
  var timer = 0;
  var waits = 0;
  var running = false;
  var again = false;
  var leaving = false;
  var leaveTimer = 0;
  var runs = [];

  function urlOf(u) {
    try { return new URL(String(u), location.href); } catch (e) { return null; }
  }
  function isFull(c) {
    return !!(c && c.items && typeof c.item_count === 'number');
  }
  function warn(msg, e) {
    if (window.console && console.warn) console.warn('[myselfie] cart rules: ' + msg, e || '');
  }

  /* ---------- 1. what happened to the cart ---------- */

  /** A finished cart write (not ours): is the cart still right? */
  function observed(u, status, body) {
    var url = urlOf(u);
    if (!url || !CART_WRITE.test(url.pathname) || url.search.indexOf(KIT.FREE_PROPS_TAG) > -1) return;
    if (!(status >= 200 && status < 300)) return; // refused (422, 429, …): the cart did not change
    if (isFull(body)) {
      known = body; // change.js / update.js / clear.js answer with the whole cart
    } else if (body && (Array.isArray(body.items) || body.variant_id)) {
      // add.js answers with the added lines only: only a booth or the free props themselves can break the rule
      var items = Array.isArray(body.items) ? body.items : [body];
      var relevant = items.some(function (it) {
        return it && (it.variant_id === C.freeProps || (C.cameraType && it.product_type === C.cameraType));
      });
      if (!relevant) return;
      known = null;
    } else {
      known = null; // HTML or nothing: unknown
    }
    seq++;
    if (known && KIT.freePropsOk(known, C)) return;
    schedule(DEBOUNCE);
  }

  var prevFetch = window.fetch;
  if (typeof prevFetch === 'function') {
    window.fetch = function (input) {
      var p = prevFetch.apply(this, arguments);
      var u = input && typeof input === 'object' && 'url' in input ? input.url : input;
      var url = urlOf(u);
      if (url && CART_WRITE.test(url.pathname)) {
        p.then(function (r) {
          var copy;
          try { copy = r.clone(); } catch (e) { copy = null; }
          if (!copy) return observed(u, r.status, null);
          copy.json().then(function (body) { observed(u, r.status, body); }, function () { observed(u, r.status, null); });
        }, function () { /* network error: nothing changed that we know of */ });
      }
      return p;
    };
  }
  if (window.XMLHttpRequest) {
    var xOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, u) {
      var url = urlOf(u);
      if (url && CART_WRITE.test(url.pathname)) {
        this.addEventListener('loadend', function () {
          var body = null;
          try {
            body = this.responseType === 'json' ? this.response : JSON.parse(this.responseText);
          } catch (e) { body = null; }
          observed(u, this.status, body);
        });
      }
      return xOpen.apply(this, arguments);
    };
  }

  // Theme writers announce their final cart (booking module, cart page, product page, season guard, this file):
  // a wrong one is fixed right away - inside the module's queue before anything waiting for it goes on
  // (e.g. the bundle dialog waits for the module to be idle, then goes to /cart)
  document.addEventListener('myselfie:cart', function (e) {
    var d = e && e.detail;
    if (!d || !isFull(d.cart)) return;
    known = d.cart;
    if (!KIT.freePropsOk(known, C)) schedule(0);
  });
  // Upcart (an app embed that may keep its own fetch) announces its own drawer edits
  window.addEventListener('aftersell-upcart:public-events:cart-updated', function () {
    if (running) return;
    known = null;
    schedule(800);
  });

  /* ---------- 2. who may write now ---------- */

  function cartPage() {
    var root = document.querySelector('[data-mys-cart]');
    return root && root.__mysCart ? root.__mysCart : null;
  }
  /** {checkout} | {wait} | {live: element} | {} for the booking modules on the page. */
  function moduleState() {
    var list = document.querySelectorAll('myselfie-product-selector');
    var live = null;
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      var cs = el.cartSync;
      if (el.booked || (el.state && el.state.busy) || (cs && cs.clearing)) return { checkout: true };
      if (!cs || !cs.enabled || cs.status === 'blocked' || typeof el.enqueue !== 'function') continue;
      if (!cs.ready) {
        if (cs.reading || !cs.loadFailedAt) return { wait: true }; // its first read is on the way
        continue; // its first read failed: it writes nothing until it has one
      }
      if (!live && typeof el.liveActive === 'function' && el.liveActive()) live = el;
    }
    return { live: live };
  }

  function schedule(ms) {
    if (leaving) return;
    clearTimeout(timer);
    timer = setTimeout(run, ms == null ? DEBOUNCE : ms);
  }
  function later() {
    waits++;
    schedule(WAIT);
  }

  function run() {
    timer = 0;
    if (leaving) return;
    if (running) { again = true; return; }
    if (known && KIT.freePropsOk(known, C)) { waits = 0; return; }
    var page = cartPage();
    if (page) {
      var s = page.state || {};
      if (s.busy) return; // "Gå til betaling": kit.addAutomaticLines runs there
      if (s.busyKey || s.adding || page._rules || page._seasonBusy) {
        if (waits < MAX_WAITS) later(); // it runs its rules after its write
        return;
      }
      waits = 0;
      // re-read + rules (free props in its queue), announced by setCart; a cart its rules leave as it is is not
      // announced again, so this cannot repeat
      return page.reload();
    }
    var ms = moduleState();
    if (ms.checkout) { waits = 0; return; } // the booking adds the free props itself
    if (ms.wait && waits < MAX_WAITS) return later(); // a module that never reads the cart: on our own below
    waits = 0;
    if (ms.live) {
      var el = ms.live;
      return sync(function (job) {
        return el.enqueue(function () {
          if (el.booked || (el.state && el.state.busy)) return null;
          // inside its queue the module's cart is current, unless a write of someone else came after it
          var cs = el.cartSync;
          return job(known && cs.cart === known ? cs.cart : null);
        });
      }, el);
    }
    sync(KIT.serial, null);
  }

  /**
   * Reconciles the free props through `via` (a queue that calls job(base)): base = a cart known to be current, else
   * the cart is read first. owner = the module that adopts the cart.
   */
  function sync(via, owner) {
    var now = Date.now();
    runs = runs.filter(function (t) { return now - t < 30000; });
    if (runs.length >= 4) { warn('too many syncs, paused'); return; }
    runs.push(now);
    running = true;
    var start = seq;
    var before = null;
    via(function (base) {
      if (leaving) return null;
      return (isFull(base) ? Promise.resolve(base) : KIT.cartGet()).then(function (cart) {
        before = cart;
        if (KIT.freePropsOk(cart, C)) return cart;
        return KIT.syncFreeProps(cart, C);
      });
    }).then(function (cart) {
      running = false;
      if (isFull(cart)) {
        if (seq === start) known = cart;
        if (cart !== before) announce(cart, owner, true);
      }
      finish(start);
    }, function (e) {
      running = false;
      warn('sync failed', e);
      finish(start);
    });
  }
  function finish(start) {
    if (again || seq !== start) {
      again = false;
      schedule(DEBOUNCE);
    }
  }
  /** "myselfie:cart" for the header and the page (a module adopts the cart and announces it itself). */
  function announce(cart, owner, changed) {
    if (owner && typeof owner.adoptOwnCart === 'function') {
      try { owner.adoptOwnCart(cart); return; } catch (e) { warn('module adopt', e); }
    }
    try { document.dispatchEvent(new CustomEvent('myselfie:cart', { detail: KIT.cartDetail(cart) })); } catch (e) { /* old browser */ }
    if (!changed) return;
    try { if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart(); } catch (e) { /* ignore */ }
  }

  /* ---------- 3. page life ---------- */

  // A navigation is under way (e.g. the bundle dialog goes to /cart): the next page checks the cart itself.
  function leave() {
    leaving = true;
    clearTimeout(timer);
    clearTimeout(leaveTimer);
    leaveTimer = setTimeout(function () { leaving = false; }, 5000); // still here: the navigation did not happen
  }
  window.addEventListener('beforeunload', leave);
  window.addEventListener('pagehide', leave);

  // Another tab may have changed the cart: one read when the page comes back (the module and the cart page re-read
  // the cart themselves and announce it).
  var hiddenAt = 0;
  var lastCheck = 0;
  function recheck() {
    var now = Date.now();
    if (now - lastCheck < 10000) return;
    lastCheck = now;
    if (cartPage() || moduleState().live) return;
    if (running) { again = true; return; }
    KIT.serial(function () { return KIT.cartGet(); }).then(function (cart) {
      if (!isFull(cart)) return;
      known = cart;
      announce(cart, null, false); // the header count may be stale too
      if (!KIT.freePropsOk(cart, C)) schedule(0);
    }, function () { /* 429 budget used up / offline: the next write or focus tries again */ });
  }
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
    if (hiddenAt && Date.now() - hiddenAt > 2000) recheck();
  });
  window.addEventListener('pageshow', function (e) {
    clearTimeout(leaveTimer);
    leaving = false;
    if (e && e.persisted) { lastCheck = 0; recheck(); }
  });

  // The cart as the server rendered it: wrong -> one sync once the page's own cart code is ready
  function initial() {
    var booths = +CFG.booths || 0;
    var lines = +CFG.freeLines || 0;
    var ok = booths ? lines === 1 && +CFG.freeQty === booths : !lines;
    if (!ok) schedule(250);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initial);
  else initial();

  // exposed for QA
  M.cartRulesDebug = function () {
    return { known: known, running: running, runs: runs.slice(), leaving: leaving, seq: seq };
  };
})();
